"use client"

import * as React from "react"
import { createPortal } from "react-dom"
import { CheckIcon, PlusIcon } from "lucide-react"

import { cn } from "../../lib/utils"
import { triggerHaptic } from "../../lib/haptics"
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "./drawer"
import { MobileSearchField } from "./mobile-search-field"
import { MOBILE_SHEET_PANEL_ATTRIBUTE, useMobileSheetHost } from "./mobile-sheet-host"

/** The phone picker's own words, in the app's language (the package only knows English defaults). */
export type MobilePickerLabels = {
  done: string
  search: string
  clearSearch: string
  noResults: string
  /** The row that keeps a typed value no option matches (creatable selects). */
  create: (query: string) => string
  /** The "none" row of a clearable select. */
  clear: string
}

const DEFAULT_LABELS: MobilePickerLabels = {
  done: "Done",
  search: "Search",
  clearSearch: "Clear search",
  noResults: "No results",
  create: (query) => `Use "${query}"`,
  clear: "None",
}

const MobilePickerLabelsContext = React.createContext<MobilePickerLabels>(DEFAULT_LABELS)

export function MobilePickerLabelsProvider({
  labels,
  children,
}: {
  labels: Partial<MobilePickerLabels>
  children: React.ReactNode
}) {
  const value = React.useMemo(() => ({ ...DEFAULT_LABELS, ...labels }), [labels])
  return <MobilePickerLabelsContext.Provider value={value}>{children}</MobilePickerLabelsContext.Provider>
}

export function useMobilePickerLabels(): MobilePickerLabels {
  return React.useContext(MobilePickerLabelsContext)
}

export type MobilePickerOption = {
  value: string
  /** What the row shows. Usually text; a node when the caller draws its own row. */
  label: React.ReactNode
  /** The text the search matches, when `label` is not a plain string. */
  searchText?: string
  /** A second line (a balance, a code, why it cannot be picked). */
  description?: string
  /** A small pill on the right (e.g. "Restreint"). */
  badge?: string
  disabled?: boolean
  /** Rendered before the label (an avatar, a colour dot, an icon). */
  leading?: React.ReactNode
  /** A section heading: consecutive options with the same group sit under one heading. */
  group?: React.ReactNode
  /** Draws the row in the destructive colour (a menu's "Delete"). */
  destructive?: boolean
}

/**
 * Server search, for lists too long to load (members): the list hands over what is typed and shows `options`
 * as given, without filtering them again.
 */
export type MobilePickerSearch = {
  onQueryChange?: (query: string) => void
  loading?: boolean
}

/** One tab of a picker that chooses between kinds (Membre / Groupe / Département). */
export type MobilePickerTab = {
  value: string
  label: string
  options: MobilePickerOption[]
  searchPlaceholder?: string
} & MobilePickerSearch

/** A row under the list that is not an option: "Create a new category". */
export type MobilePickerAction = {
  label: React.ReactNode
  onSelect: () => void
}

export type MobilePickerListProps = {
  /** The selected option's value (in the active tab when `tabs` is set). */
  value?: string | null
  /** Multi-select: the chosen values. Rows then toggle, with a check on each chosen one. */
  values?: readonly string[]
  /** Called with the picked value and, for a tabbed picker, the tab it came from. */
  onSelect: (value: string, tab?: string) => void
  options?: MobilePickerOption[]
  tabs?: MobilePickerTab[]
  /** The tab shown first (a tabbed picker opens on the kind already chosen). */
  initialTab?: string
  searchPlaceholder?: string
  emptyLabel?: React.ReactNode
  /** A "none" row under the list (e.g. "Anonyme, sans donateur"); picking it calls `onClear`. */
  clearLabel?: string
  onClear?: () => void
  /** Search on or off. Left out, a list longer than a few rows gets one (a short fixed list does not). */
  searchable?: boolean
  /** Creatable: typing something no option matches offers a row that keeps it. */
  onCreate?: (query: string) => void
  /** How the create row reads (defaults to the provider's `create`). */
  formatCreateLabel?: (query: string) => string
  /** Rows under the list that act rather than pick ("Create a new category"). */
  actions?: MobilePickerAction[]
  /** Free content under everything (a hint, a link). */
  footer?: React.ReactNode
} & MobilePickerSearch

/** More options than this and a sheet opens tall, so searching does not resize it; fewer and it needs no search. */
const TALL_SHEET_MIN_OPTIONS = 6

function normalize(text: string) {
  return text.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase()
}

function textOf(node: React.ReactNode): string {
  if (typeof node === "string" || typeof node === "number") return String(node)
  if (Array.isArray(node)) return node.map(textOf).join(" ")
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) return textOf(node.props.children)
  return ""
}

/** Whether a picker is long enough (or server-searched) to deserve a tall, fixed-height sheet. */
export function isTallPicker({
  options = [],
  tabs,
  onQueryChange,
}: Pick<MobilePickerListProps, "options" | "tabs" | "onQueryChange">) {
  const longest = Math.max(options.length, ...(tabs ?? []).map((item) => item.options.length))
  return longest > TALL_SHEET_MIN_OPTIONS || Boolean(onQueryChange) || Boolean(tabs?.some((item) => item.onQueryChange))
}

/** The grouped card every phone list uses: rounded, muted, one hairline between rows. */
export const MOBILE_PICKER_CARD_CLASS = "divide-y divide-border/60 overflow-hidden rounded-2xl bg-muted/60"
export const MOBILE_PICKER_ROW_CLASS =
  "flex min-h-[3.25rem] w-full items-center gap-3 px-4 py-2 text-left text-base font-semibold text-foreground active:bg-muted disabled:opacity-55 [&_svg:not([class*='size-'])]:size-5 [&_svg]:shrink-0"
export const MOBILE_PICKER_GROUP_LABEL_CLASS = "px-1 pt-2 pb-1.5 text-[13px] font-bold text-muted-foreground"

/**
 * The picking itself: an optional segmented control between kinds, the app's phone search field, the options as
 * a grouped list with a check on the current one(s), options that cannot be picked greyed with the reason, and an
 * optional "none" row. Lives in `MobilePickerSheet`, or inside another sheet as one of its screens so picking never
 * stacks a second sheet. It starts fresh each time it mounts.
 */
export function MobilePickerList({
  value,
  values,
  onSelect,
  options = [],
  tabs,
  initialTab,
  searchPlaceholder,
  emptyLabel,
  clearLabel,
  onClear,
  onQueryChange,
  loading = false,
  searchable,
  onCreate,
  formatCreateLabel,
  actions,
  footer,
}: MobilePickerListProps) {
  const labels = useMobilePickerLabels()
  const [query, setQuery] = React.useState("")
  const [tab, setTab] = React.useState(initialTab ?? tabs?.[0]?.value ?? "")

  React.useEffect(() => {
    // A fresh list: clear any search left in a server lookup from last time.
    onQueryChange?.("")
    for (const item of tabs ?? []) item.onQueryChange?.("")
    // Mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const activeTab = tabs?.find((item) => item.value === tab) ?? tabs?.[0]
  const source = activeTab ? activeTab.options : options
  const searchHandler = activeTab ? activeTab.onQueryChange : onQueryChange
  const isLoading = activeTab ? Boolean(activeTab.loading) : loading
  const showSearch = searchable ?? (Boolean(searchHandler) || Boolean(onCreate) || source.length > TALL_SHEET_MIN_OPTIONS)
  const changeQuery = (next: string) => {
    setQuery(next)
    searchHandler?.(next)
  }
  const filtered = React.useMemo(() => {
    // A server-searched list arrives already filtered.
    if (searchHandler) return source
    const needle = normalize(query.trim())
    if (!needle) return source
    return source.filter((option) =>
      normalize(`${option.searchText ?? textOf(option.label)} ${option.description ?? ""}`).includes(needle),
    )
  }, [query, searchHandler, source])

  const trimmed = query.trim()
  const canCreate =
    Boolean(onCreate) &&
    trimmed.length > 0 &&
    !source.some((option) => normalize(option.searchText ?? textOf(option.label)) === normalize(trimmed))

  // Consecutive options sharing a group sit in one card under its heading.
  const sections = React.useMemo(() => {
    const result: Array<{ key: string; heading: React.ReactNode; options: MobilePickerOption[] }> = []
    for (const option of filtered) {
      const heading = option.group ?? null
      const last = result[result.length - 1]
      if (last && last.heading === heading) last.options.push(option)
      else result.push({ key: `${result.length}-${option.value}`, heading, options: [option] })
    }
    return result
  }, [filtered])

  const isChosen = (option: MobilePickerOption) => (values ? values.includes(option.value) : option.value === value)

  return (
    <div className='flex min-h-0 flex-1 flex-col gap-3'>
      {tabs && tabs.length > 1 ? (
        <div
          role='tablist'
          className='flex gap-0.5 rounded-[0.875rem] bg-[rgba(118,118,128,0.12)] p-[3px] dark:bg-[rgba(118,118,128,0.24)]'
        >
          {tabs.map((item) => (
            <button
              key={item.value}
              type='button'
              role='tab'
              aria-selected={item.value === activeTab?.value}
              onClick={() => {
                setTab(item.value)
                setQuery("")
                item.onQueryChange?.("")
              }}
              className={cn(
                "flex h-9 flex-1 items-center justify-center rounded-[0.6875rem] text-[13px] font-bold transition-colors",
                item.value === activeTab?.value ? "bg-card text-foreground font-extrabold shadow-sm" : "text-muted-foreground",
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
      {/* The app's one phone search field (lists, members, departments), not a second look-alike. */}
      {showSearch ? (
        <MobileSearchField
          value={query}
          onValueChange={changeQuery}
          placeholder={activeTab?.searchPlaceholder ?? searchPlaceholder ?? labels.search}
          clearLabel={labels.clearSearch}
        />
      ) : null}
      <div className='flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto'>
        {canCreate ? (
          <button
            type='button'
            onClick={() => onCreate?.(trimmed)}
            className={cn(MOBILE_PICKER_CARD_CLASS, MOBILE_PICKER_ROW_CLASS, "text-primary")}
          >
            <PlusIcon aria-hidden />
            <span className='min-w-0 flex-1 truncate'>{(formatCreateLabel ?? labels.create)(trimmed)}</span>
          </button>
        ) : null}
        {filtered.length === 0 ? (
          canCreate ? null : (
            <p className='text-muted-foreground py-8 text-center text-sm font-semibold'>
              {isLoading ? (
                <span className='border-muted-foreground/40 inline-block size-5 animate-spin rounded-full border-2 border-t-transparent' />
              ) : (
                (emptyLabel ?? labels.noResults)
              )}
            </p>
          )
        ) : (
          sections.map((section) => (
            <div key={section.key} className='flex flex-col'>
              {section.heading ? <p className={MOBILE_PICKER_GROUP_LABEL_CLASS}>{section.heading}</p> : null}
              <ul className={MOBILE_PICKER_CARD_CLASS}>
                {section.options.map((option) => {
                  const chosen = isChosen(option)
                  return (
                    <li key={option.value}>
                      <button
                        type='button'
                        role={values ? "menuitemcheckbox" : "option"}
                        aria-checked={values ? chosen : undefined}
                        aria-selected={values ? undefined : chosen}
                        disabled={option.disabled}
                        onClick={() => {
                          triggerHaptic("selection")
                          onSelect(option.value, activeTab?.value)
                        }}
                        className={cn(MOBILE_PICKER_ROW_CLASS, option.destructive && "text-destructive")}
                      >
                        {option.leading}
                        <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
                          {/* Two lines, then "…": long names that start alike stay tell-apart-able. */}
                          <span className='line-clamp-2 break-words'>{option.label}</span>
                          {option.description ? (
                            <span className='text-muted-foreground truncate text-xs font-semibold'>{option.description}</span>
                          ) : null}
                        </span>
                        {option.badge ? (
                          <span className='bg-muted text-muted-foreground shrink-0 rounded-full px-2 py-0.5 text-[11px] font-extrabold'>
                            {option.badge}
                          </span>
                        ) : null}
                        {chosen ? <CheckIcon aria-hidden className='text-primary size-5 shrink-0' strokeWidth={3} /> : null}
                      </button>
                    </li>
                  )
                })}
              </ul>
            </div>
          ))
        )}
        {/* The "none" choice is hidden while searching: under "no results" it read as a second empty message. */}
        {clearLabel && onClear && !trimmed ? (
          <button
            type='button'
            onClick={onClear}
            className={cn(MOBILE_PICKER_CARD_CLASS, MOBILE_PICKER_ROW_CLASS, "text-muted-foreground")}
          >
            {clearLabel}
          </button>
        ) : null}
        {actions && actions.length > 0 ? (
          <div className={MOBILE_PICKER_CARD_CLASS}>
            {actions.map((action, index) => (
              <button
                key={index}
                type='button'
                onClick={action.onSelect}
                className={cn(MOBILE_PICKER_ROW_CLASS, "text-primary")}
              >
                <PlusIcon aria-hidden />
                <span className='min-w-0 flex-1 truncate'>{action.label}</span>
              </button>
            ))}
          </div>
        ) : null}
        {footer}
      </div>
    </div>
  )
}

/** The bottom sheet's frame: title on the left, Done on the right, the content below with the safe area. */
export function MobilePickerSheetFrame({
  open,
  onOpenChange,
  title,
  description,
  doneLabel,
  tall = false,
  children,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: React.ReactNode
  description?: React.ReactNode
  doneLabel?: string
  tall?: boolean
  children: React.ReactNode
}) {
  const labels = useMobilePickerLabels()
  const host = useMobileSheetHost()
  const header = (
    <>
      <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
        {host ? (
          <span className='truncate text-lg font-extrabold'>{title}</span>
        ) : (
          <>
            <DrawerTitle className='truncate text-lg font-extrabold'>{title}</DrawerTitle>
            <DrawerDescription className={description ? "text-muted-foreground text-sm" : "sr-only"}>
              {description ?? textOf(title)}
            </DrawerDescription>
          </>
        )}
        {host && description ? <span className='text-muted-foreground text-sm'>{description}</span> : null}
      </span>
      <button
        type='button'
        onClick={() => onOpenChange(false)}
        className='text-primary active:bg-primary/10 rounded-full px-2 py-1 text-base font-bold'
      >
        {doneLabel ?? labels.done}
      </button>
    </>
  )

  if (host) {
    // Opened from inside a sheet: a screen of that sheet (Done comes back), never a second sheet on top.
    if (!open) return null
    return createPortal(
      <div
        {...{ [MOBILE_SHEET_PANEL_ATTRIBUTE]: tall ? "tall" : "short" }}
        role='dialog'
        aria-modal='true'
        aria-label={textOf(title)}
        className='bg-card animate-in fade-in-0 slide-in-from-right-6 pointer-events-auto absolute inset-0 flex flex-col duration-200'
        onKeyDown={(event) => {
          // Enter in the search field must not submit the form the sheet holds; Escape closes only this screen.
          if (event.key === "Enter" && event.target instanceof HTMLInputElement) event.preventDefault()
          if (event.key === "Escape") {
            event.stopPropagation()
            onOpenChange(false)
          }
        }}
      >
        <div className='flex items-center justify-between gap-3 px-4 pt-5 pb-3'>{header}</div>
        <div className='flex min-h-0 flex-1 flex-col px-4 pb-[max(1rem,env(safe-area-inset-bottom))]'>{children}</div>
      </div>,
      host,
    )
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className={tall ? "h-[88dvh] max-h-[88dvh]" : "max-h-[88dvh]"}>
        <DrawerHeader className='flex-row items-center justify-between group-data-[vaul-drawer-direction=bottom]/drawer-content:text-left'>
          {header}
        </DrawerHeader>
        <div className='flex min-h-0 flex-1 flex-col px-4 pb-[max(1rem,env(safe-area-inset-bottom))]'>{children}</div>
      </DrawerContent>
    </Drawer>
  )
}

export type MobilePickerSheetProps = MobilePickerListProps & {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: React.ReactNode
  description?: React.ReactNode
  doneLabel?: string
}

/**
 * Every choice on a phone: the app's bottom sheet holding a `MobilePickerList`. Picking closes it (a multi-select
 * stays open until Done). A long or server-searched list opens tall and keeps that height while the search
 * narrows it.
 */
export function MobilePickerSheet({
  open,
  onOpenChange,
  title,
  description,
  doneLabel,
  onSelect,
  onClear,
  onCreate,
  actions,
  ...list
}: MobilePickerSheetProps) {
  const [tall, setTall] = React.useState(false)
  const [session, setSession] = React.useState(0)
  const multiple = Boolean(list.values)

  React.useEffect(() => {
    if (!open) return
    // Decided once per opening, and a new list (fresh search and tab) each time.
    setTall(isTallPicker(list))
    setSession((current) => current + 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const close = () => onOpenChange(false)

  return (
    <MobilePickerSheetFrame
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      doneLabel={doneLabel}
      tall={tall}
    >
      <MobilePickerList
        key={session}
        {...list}
        onSelect={(next, tab) => {
          onSelect(next, tab)
          if (!multiple) close()
        }}
        onClear={
          onClear
            ? () => {
                onClear()
                close()
              }
            : undefined
        }
        onCreate={
          onCreate
            ? (query) => {
                onCreate(query)
                if (!multiple) close()
              }
            : undefined
        }
        actions={actions?.map((action) => ({
          ...action,
          onSelect: () => {
            close()
            action.onSelect()
          },
        }))}
      />
    </MobilePickerSheetFrame>
  )
}
